import { useEffect, useRef, useState, type ImgHTMLAttributes } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

export function PhotoLightbox({
  src,
  fullSrc,
  alt,
  className,
  imgClassName,
  imgProps,
}: {
  src: string;
  /** What the full-screen view loads. Defaults to `src`. */
  fullSrc?: string;
  alt: string;
  className?: string;
  imgClassName?: string;
  /** `srcSet`, `sizes`, the intrinsic size, the fetch priority: the inline image only. */
  imgProps?: Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt' | 'className'>;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const opener = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const openerNode = opener.current;
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = prevOverflow;
      // Back to the photo that opened it: a keyboard user is otherwise dropped
      // at the top of the page.
      openerNode?.focus();
    };
  }, [open]);

  return (
    <div className={className}>
      <img
        {...imgProps}
        src={src}
        alt={alt}
        className={`pointer-events-none select-none ${imgClassName ?? ''}`}
      />
      <button
        ref={opener}
        type="button"
        onClick={() => setOpen(true)}
        className="absolute inset-0 w-full h-full cursor-zoom-in rounded-[inherit]"
        aria-label={t('common.fullPhoto')}
      />
      {open &&
        createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-label={alt}
            className="on-ink fixed inset-0 z-50 bg-primary/90 flex items-center justify-center p-4 select-none"
            onClick={() => setOpen(false)}
          >
            <img
              src={fullSrc ?? src}
              alt={alt}
              className="max-w-full max-h-full object-contain rounded-xl select-none"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              type="button"
              className="absolute top-4 right-4 w-12 h-12 rounded-full bg-on-primary/10 hover:bg-on-primary/20 text-on-primary flex items-center justify-center transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
              }}
              aria-label={t('common.close')}
            >
              <span className="material-symbols-outlined text-[24px]">close</span>
            </button>
          </div>,
          document.body,
        )}
    </div>
  );
}
